import * as React from 'react';
import type { MeResponse } from '@albuelis-accounting/shared';
import { getMe } from '@/api/me';
import { useAuth } from '@/auth/AuthProvider';

/**
 * The current user's `/me` document.
 *
 * Fetched once after the session is established and shared through context, because
 * several pieces of chrome need it — the environment badge, and the feature flags
 * that hide controls a deployment cannot support (no Mercury, no AWS usage). It is
 * `undefined` until the request resolves, so every consumer treats it as optional and
 * falls back to the configuration document's values in the meantime.
 */
const MeContext = React.createContext<MeResponse | undefined>(undefined);

export function MeProvider({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const [me, setMe] = React.useState<MeResponse | undefined>(undefined);

  React.useEffect(() => {
    if (status !== 'authenticated') return;
    const controller = new AbortController();
    getMe(controller.signal).then(
      (result) => setMe(result),
      (error) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        // Not fatal: the badge falls back to the config value and feature flags
        // default to shown. Logged for whoever is debugging a missing control.
        console.warn('Could not load /me; falling back to configuration defaults.', error);
      },
    );
    return () => controller.abort();
  }, [status]);

  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}

/** The `/me` document, or `undefined` while it loads. */
export function useMe(): MeResponse | undefined {
  return React.useContext(MeContext);
}
